import { MutableRefObject, useCallback, useMemo } from 'react';
import { DataGridRef } from 'devextreme-react/data-grid';
import { useAuth } from 'react-oidc-context';
import { useConfirmDialog } from 'src/@core/hooks/useConfirmDialog';

const useStateStorage = ({
  enabled,
  name,
  defaultState,
  ref,
  filterColumn,
  onStateChange
}: {
  enabled: boolean;
  name: string;
  defaultState: any;
  ref: MutableRefObject<DataGridRef<any, any> | null>;
  filterColumn?: string;
  onStateChange?: (state: any) => void;
}) => {
  const { set } = useConfirmDialog();
  const auth = useAuth();
  const user = auth.user;
  const loggedInUserId = user?.profile.tenantUserId as string;

  const gridStateStorageKey = `gridState-${name?.toLowerCase()}-${
    filterColumn ? `${filterColumn.toLocaleLowerCase()}-` : ''
  }${loggedInUserId}`;

  const saveDefaultToStorage = useCallback(
    (state: any) => {
      localStorage.setItem(gridStateStorageKey, state);
    },
    [gridStateStorageKey]
  );

  /**
   * Saves the grid state to local storage if the grid is enabled.
   *
   * @return {void}
   */
  const saveGridStateToStorage = useCallback(() => {
    if (!enabled) return;

    if (ref?.current) {
      const state = ref.current.instance().state();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { pageIndex, ...rest } = state;

      // check if state contains any keys that are numbers
      const keys = Object.keys(rest);
      const hasKeys = keys.some(key => {
        const num = parseInt(key, 10);

        return !isNaN(num) && num.toString() === key;
      });

      if (hasKeys) {
        // Do we have existing state?
        const existing = localStorage.getItem(gridStateStorageKey);
        if (existing) {
          // Don't overwrite existing state
          return;
        }
      }

      rest.selectedRowKeys = [];

      localStorage.setItem(gridStateStorageKey, JSON.stringify({ pageIndex: 0, ...rest }));

      if (typeof onStateChange === 'function') {
        onStateChange(state);
      }
    }
  }, [enabled, gridStateStorageKey, onStateChange, ref]);

  /**
   * Fetches the stored grid state from local storage if the grid is enabled.
   * If no state is found, the default state is saved to local storage.
   *
   */
  const fetchStoredGridState = useCallback(() => {
    if (!enabled) return;

    let state = localStorage.getItem(gridStateStorageKey);

    if (!state && defaultState) {
      saveDefaultToStorage(defaultState);

      state = defaultState;
    } else {
      state = JSON.parse(state ?? '{}');
    }

    if (ref?.current) {
      ref?.current?.instance()?.state?.(state);
    }
  }, [enabled, defaultState, gridStateStorageKey, ref, saveDefaultToStorage]);

  /**
   * Resets the state of the grid.
   *
   * @return {void}
   */
  const resetState = useCallback(() => {
    if (!enabled) return;

    set(
      'Are you sure you wish to reset grid?',
      () => {
        localStorage.removeItem(gridStateStorageKey);
        fetchStoredGridState();
      },
      undefined,
      'this action cannot be undone'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fetchStoredGridState, gridStateStorageKey]);

  return useMemo(
    () => ({
      fetchStoredGridState,
      saveGridStateToStorage,
      resetState,
      storageKey: gridStateStorageKey
    }),
    [fetchStoredGridState, saveGridStateToStorage, resetState, gridStateStorageKey]
  );
};
export default useStateStorage;
